import React, { useCallback, useEffect, useState } from 'react'
import {
  Alert, Badge, Button, Card, Col, Container, Form, Modal, Nav, Row, Spinner, Tab, Table
} from 'react-bootstrap'
import Swal from 'sweetalert2'
import Header from '../layouts/Header'
import Footer from '../layouts/Footer'
import axiosInstance from '../services/axiosInterceptor'
import ThaiDateInput from '../components/ThaiDateInput'
import {
  EMPLOYMENT_TYPES, LICENSE_TYPES, STAFF_STATUS, UPLOAD_BASE,
  docExpiryState, documentFormData, fmtDateTH, licenseFormData,
} from './hrShared'

// ช่องที่เจ้าตัวแก้เองได้ — ตรงกับ SELF_PROFILE_FIELDS (+ t_phone) ฝั่ง server
const emptyForm = {
  t_phone: '', hp_address: '', hp_education_level: '',
  hp_position_title: '', hp_department: '', hp_employment_type: '', hp_hire_date: '',
  hp_emergency_name: '', hp_emergency_phone: '', hp_emergency_relation: '',
}

const emptyDocForm = { hd_type: '', hd_title: '', hd_issue_date: '', hd_expire_date: '', file: null }

export default function MyHrProfile() {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const [form, setForm] = useState(emptyForm)
  const [saving, setSaving] = useState(false)

  const [docForm, setDocForm] = useState(emptyDocForm)
  const [uploading, setUploading] = useState(false)

  const [eduModal, setEduModal] = useState(null)   // null | {he_id?, ...}
  const [workModal, setWorkModal] = useState(null) // null | {hw_id?, ...}
  const [licModal, setLicModal] = useState(null)   // null | {hl_id?, ...}
  const [docModal, setDocModal] = useState(null)   // null | {hd_id, ...} — แก้เอกสารที่อัปโหลดแล้ว
  const [savingItem, setSavingItem] = useState(false)

  const fetchProfile = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/api/hr/me/profile')
      if (res.data.success) {
        const d = res.data.data
        setData(d)
        setForm(Object.fromEntries(Object.keys(emptyForm).map(k => [k, d[k] ?? ''])))
      }
    } catch (err) {
      setError(err.response?.data?.message || 'โหลดข้อมูลไม่สำเร็จ')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchProfile() }, [fetchProfile])

  const handleSaveProfile = async (e) => {
    e.preventDefault()
    setSaving(true)
    try {
      const res = await axiosInstance.put('/api/hr/me/profile', form)
      if (res.data.success) {
        Swal.fire({ icon: 'success', title: 'บันทึกข้อมูลแล้ว', timer: 1500, showConfirmButton: false })
        fetchProfile()
      }
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'บันทึกไม่สำเร็จ', text: err.response?.data?.message || '' })
    } finally {
      setSaving(false)
    }
  }

  const handleSaveEducation = async (e) => {
    e.preventDefault()
    setSavingItem(true)
    try {
      await axiosInstance.post('/api/hr/me/education', eduModal)
      setEduModal(null)
      fetchProfile()
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'บันทึกไม่สำเร็จ', text: err.response?.data?.message || '' })
    } finally {
      setSavingItem(false)
    }
  }

  const handleSaveWork = async (e) => {
    e.preventDefault()
    setSavingItem(true)
    try {
      await axiosInstance.post('/api/hr/me/work-history', workModal)
      setWorkModal(null)
      fetchProfile()
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'บันทึกไม่สำเร็จ', text: err.response?.data?.message || '' })
    } finally {
      setSavingItem(false)
    }
  }

  // เพิ่มใบใหม่ หรือแก้ใบเดิมเมื่อมี hl_id — ไฟล์ไม่บังคับแนบทั้งสองกรณี
  const handleSaveLicense = async (e) => {
    e.preventDefault()
    setSavingItem(true)
    try {
      await axiosInstance.post('/api/hr/me/licenses', licenseFormData(licModal))
      setLicModal(null)
      fetchProfile()
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'บันทึกไม่สำเร็จ', text: err.response?.data?.message || '' })
    } finally {
      setSavingItem(false)
    }
  }

  const handleSaveDocument = async (e) => {
    e.preventDefault()
    setSavingItem(true)
    try {
      await axiosInstance.put(`/api/hr/me/documents/${docModal.hd_id}`, documentFormData(docModal))
      setDocModal(null)
      fetchProfile()
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'บันทึกไม่สำเร็จ', text: err.response?.data?.message || '' })
    } finally {
      setSavingItem(false)
    }
  }

  const handleUploadDocument = async (e) => {
    e.preventDefault()
    if (!docForm.file) return
    setUploading(true)
    try {
      await axiosInstance.post('/api/hr/me/documents', documentFormData(docForm))
      setDocForm(emptyDocForm)
      e.target.reset()
      fetchProfile()
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'อัปโหลดไม่สำเร็จ', text: err.response?.data?.message || '' })
    } finally {
      setUploading(false)
    }
  }

  const confirmDelete = async (title, url) => {
    const result = await Swal.fire({
      icon: 'warning', title, text: 'การลบไม่สามารถย้อนกลับได้',
      showCancelButton: true, confirmButtonText: 'ลบ', cancelButtonText: 'ยกเลิก',
      confirmButtonColor: '#dc3545',
    })
    if (!result.isConfirmed) return
    try {
      await axiosInstance.delete(url)
      fetchProfile()
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'ลบไม่สำเร็จ', text: err.response?.data?.message || '' })
    }
  }

  const st = STAFF_STATUS[data?.hp_status] || STAFF_STATUS.active

  return (
    <>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <Container fluid>
          <div className="d-flex align-items-center justify-content-between mb-4">
            <h4 className="main-title mb-0">
              <i className="ri-profile-line me-2 text-primary" />
              ประวัติของฉัน
            </h4>
          </div>

          {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

          {loading ? (
            <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
          ) : data && (
            <>
              <Card className="card-one mb-3">
                <Card.Body className="d-flex flex-wrap align-items-center gap-3">
                  <div>
                    <h5 className="mb-1">{data.t_name}</h5>
                    <div className="text-muted fs-sm">
                      รหัสประจำตัว <code>{data.t_idnumber}</code>
                      {data.t_phone && <> · โทร {data.t_phone}</>}
                    </div>
                    <div className="mt-1">
                      {(data.positions || []).map(p => (
                        <Badge bg="light" text="dark" className="me-1 border" key={p.pos_id}>{p.pos_name}</Badge>
                      ))}
                    </div>
                  </div>
                  <div className="ms-auto text-end">
                    <Badge bg={st.bg} className="fs-sm">{st.label}</Badge>
                    {data.hp_department && <div className="text-muted fs-sm mt-1">{data.hp_department}</div>}
                  </div>
                </Card.Body>
              </Card>

              <Tab.Container defaultActiveKey="profile">
                <Card className="card-one">
                  <Card.Header>
                    <Nav variant="tabs" className="card-header-tabs">
                      <Nav.Item><Nav.Link eventKey="profile">ข้อมูลส่วนตัว</Nav.Link></Nav.Item>
                      <Nav.Item><Nav.Link eventKey="education">การศึกษา</Nav.Link></Nav.Item>
                      <Nav.Item><Nav.Link eventKey="work">ประวัติการทำงาน</Nav.Link></Nav.Item>
                      <Nav.Item><Nav.Link eventKey="licenses">ใบประกอบวิชาชีพ</Nav.Link></Nav.Item>
                      <Nav.Item><Nav.Link eventKey="documents">เอกสาร</Nav.Link></Nav.Item>
                    </Nav>
                  </Card.Header>
                  <Card.Body>
                    <Tab.Content>
                      <Tab.Pane eventKey="profile">
                        <Form onSubmit={handleSaveProfile}>
                          <Row className="g-3">
                            <Col md={3}>
                              <Form.Label className="fs-sm mb-1">ตำแหน่ง</Form.Label>
                              <Form.Control size="sm" value={form.hp_position_title}
                                onChange={e => setForm(f => ({ ...f, hp_position_title: e.target.value }))} />
                            </Col>
                            <Col md={3}>
                              <Form.Label className="fs-sm mb-1">ฝ่าย/แผนก</Form.Label>
                              <Form.Control size="sm" value={form.hp_department}
                                onChange={e => setForm(f => ({ ...f, hp_department: e.target.value }))} />
                            </Col>
                            <Col md={3}>
                              <Form.Label className="fs-sm mb-1">ประเภทการจ้าง</Form.Label>
                              <Form.Select size="sm" value={form.hp_employment_type}
                                onChange={e => setForm(f => ({ ...f, hp_employment_type: e.target.value }))}>
                                <option value="">— เลือก —</option>
                                {Object.entries(EMPLOYMENT_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                              </Form.Select>
                            </Col>
                            <Col md={3}>
                              <Form.Label className="fs-sm mb-1">วันที่เริ่มงาน</Form.Label>
                              <ThaiDateInput size="sm" value={form.hp_hire_date}
                                onChange={v => setForm(f => ({ ...f, hp_hire_date: v }))} />
                            </Col>
                            <Col md={12}>
                              <hr className="my-1" />
                            </Col>
                            <Col md={4}>
                              <Form.Label className="fs-sm mb-1">เบอร์โทร</Form.Label>
                              <Form.Control size="sm" value={form.t_phone}
                                onChange={e => setForm(f => ({ ...f, t_phone: e.target.value }))} />
                            </Col>
                            <Col md={4}>
                              <Form.Label className="fs-sm mb-1">วุฒิการศึกษาสูงสุด</Form.Label>
                              <Form.Control size="sm" value={form.hp_education_level}
                                onChange={e => setForm(f => ({ ...f, hp_education_level: e.target.value }))} />
                            </Col>
                            <Col md={12}>
                              <Form.Label className="fs-sm mb-1">ที่อยู่</Form.Label>
                              <Form.Control size="sm" as="textarea" rows={2} value={form.hp_address}
                                onChange={e => setForm(f => ({ ...f, hp_address: e.target.value }))} />
                            </Col>
                            <Col md={4}>
                              <Form.Label className="fs-sm mb-1">ผู้ติดต่อฉุกเฉิน</Form.Label>
                              <Form.Control size="sm" value={form.hp_emergency_name}
                                onChange={e => setForm(f => ({ ...f, hp_emergency_name: e.target.value }))} />
                            </Col>
                            <Col md={4}>
                              <Form.Label className="fs-sm mb-1">เบอร์โทรฉุกเฉิน</Form.Label>
                              <Form.Control size="sm" value={form.hp_emergency_phone}
                                onChange={e => setForm(f => ({ ...f, hp_emergency_phone: e.target.value }))} />
                            </Col>
                            <Col md={4}>
                              <Form.Label className="fs-sm mb-1">ความสัมพันธ์</Form.Label>
                              <Form.Control size="sm" value={form.hp_emergency_relation}
                                onChange={e => setForm(f => ({ ...f, hp_emergency_relation: e.target.value }))} />
                            </Col>
                          </Row>
                          <div className="mt-3 text-end">
                            <Button type="submit" size="sm" variant="primary" disabled={saving}>
                              {saving ? <Spinner size="sm" className="me-1" /> : <i className="ri-save-line me-1" />}
                              บันทึกข้อมูล
                            </Button>
                          </div>
                        </Form>
                      </Tab.Pane>

                      <Tab.Pane eventKey="education">
                        <div className="text-end mb-2">
                          <Button size="sm" variant="primary" onClick={() => setEduModal({})}>
                            <i className="ri-add-line me-1" />เพิ่มวุฒิการศึกษา
                          </Button>
                        </div>
                        <Table responsive hover className="mb-0 fs-sm">
                          <thead className="table-light">
                            <tr>
                              <th>ระดับ</th><th>วุฒิ/ปริญญา</th><th>สาขา</th><th>สถาบัน</th><th>ปีที่จบ (พ.ศ.)</th><th style={{ width: 90 }}></th>
                            </tr>
                          </thead>
                          <tbody>
                            {(data.education || []).length === 0 ? (
                              <tr><td colSpan={6} className="text-center text-muted py-4">ยังไม่มีข้อมูลการศึกษา</td></tr>
                            ) : data.education.map(ed => (
                              <tr key={ed.he_id}>
                                <td>{ed.he_level || '-'}</td>
                                <td>{ed.he_degree || '-'}</td>
                                <td>{ed.he_major || '-'}</td>
                                <td>{ed.he_institution || '-'}</td>
                                <td>{ed.he_grad_year || '-'}</td>
                                <td className="text-end">
                                  <Button variant="link" size="sm" className="p-0 me-2" onClick={() => setEduModal(ed)}>
                                    <i className="ri-edit-line" />
                                  </Button>
                                  <Button variant="link" size="sm" className="p-0 text-danger"
                                    onClick={() => confirmDelete('ลบข้อมูลการศึกษา?', `/api/hr/me/education/${ed.he_id}`)}>
                                    <i className="ri-delete-bin-line" />
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </Tab.Pane>

                      <Tab.Pane eventKey="work">
                        <div className="text-end mb-2">
                          <Button size="sm" variant="primary" onClick={() => setWorkModal({})}>
                            <i className="ri-add-line me-1" />เพิ่มประวัติการทำงาน
                          </Button>
                        </div>
                        <Table responsive hover className="mb-0 fs-sm">
                          <thead className="table-light">
                            <tr>
                              <th>ตำแหน่ง</th><th>หน่วยงาน</th><th>เริ่ม</th><th>สิ้นสุด</th><th>หมายเหตุ</th><th style={{ width: 90 }}></th>
                            </tr>
                          </thead>
                          <tbody>
                            {(data.workHistory || []).length === 0 ? (
                              <tr><td colSpan={6} className="text-center text-muted py-4">ยังไม่มีประวัติการทำงาน</td></tr>
                            ) : data.workHistory.map(w => (
                              <tr key={w.hw_id}>
                                <td>{w.hw_position || '-'}</td>
                                <td>{w.hw_organization || '-'}</td>
                                <td>{fmtDateTH(w.hw_start_date)}</td>
                                <td>{w.hw_end_date ? fmtDateTH(w.hw_end_date) : 'ปัจจุบัน'}</td>
                                <td>{w.hw_note || '-'}</td>
                                <td className="text-end">
                                  <Button variant="link" size="sm" className="p-0 me-2" onClick={() => setWorkModal(w)}>
                                    <i className="ri-edit-line" />
                                  </Button>
                                  <Button variant="link" size="sm" className="p-0 text-danger"
                                    onClick={() => confirmDelete('ลบประวัติการทำงาน?', `/api/hr/me/work-history/${w.hw_id}`)}>
                                    <i className="ri-delete-bin-line" />
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </Tab.Pane>

                      <Tab.Pane eventKey="licenses">
                        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
                          <div className="text-muted fs-sm">
                            ใบอนุญาตประกอบวิชาชีพมีอายุ 5 ปี — ต่ออายุแล้วให้บันทึกเป็นรายการใหม่
                            ใบเดิมเก็บไว้เป็นประวัติ ไม่ต้องแก้ทับ
                          </div>
                          <Button size="sm" variant="primary" onClick={() => setLicModal({ hl_type: 'teacher' })}>
                            <i className="ri-add-line me-1" />เพิ่มใบอนุญาต
                          </Button>
                        </div>
                        <Table responsive hover className="mb-0 fs-sm">
                          <thead className="table-light">
                            <tr>
                              <th>ประเภท</th><th>เลขที่ใบอนุญาต</th><th>วันออก</th><th>วันหมดอายุ</th>
                              <th>ไฟล์</th><th>บันทึกโดย</th><th style={{ width: 90 }}></th>
                            </tr>
                          </thead>
                          <tbody>
                            {(data.licenses || []).length === 0 ? (
                              <tr><td colSpan={7} className="text-center text-muted py-4">ยังไม่มีใบอนุญาตประกอบวิชาชีพ</td></tr>
                            ) : data.licenses.map(l => {
                              const exp = docExpiryState(l.hl_expire_date)
                              return (
                                <tr key={l.hl_id}>
                                  <td>{LICENSE_TYPES[l.hl_type] || l.hl_type}</td>
                                  <td>
                                    <code>{l.hl_number}</code>
                                    {l.hl_note && <div className="text-muted">{l.hl_note}</div>}
                                  </td>
                                  <td>{l.hl_issue_date ? fmtDateTH(l.hl_issue_date) : '-'}</td>
                                  <td>
                                    {l.hl_expire_date
                                      ? fmtDateTH(l.hl_expire_date)
                                      : <span className="text-muted">ยังไม่ระบุ</span>}
                                    {exp && <Badge bg={exp.bg} className="ms-1">{exp.label}</Badge>}
                                  </td>
                                  <td>
                                    {l.hl_filename ? (
                                      <a href={`${UPLOAD_BASE}/uploads/hr_licenses/${l.hl_filename}`}
                                        target="_blank" rel="noreferrer">
                                        <i className="ri-download-2-line me-1" />เปิดไฟล์
                                      </a>
                                    ) : <span className="text-muted">ยังไม่แนบ</span>}
                                  </td>
                                  <td>{l.uploaded_by_name || '-'}</td>
                                  <td className="text-end text-nowrap">
                                    {/* ใบที่งานบุคลากรบันทึกให้เป็นของฝ่าย เจ้าตัวแก้/ลบเองไม่ได้ (กฎเดียวกับเอกสาร) */}
                                    {l.uploaded_by === data.t_id ? (
                                      <>
                                        <Button variant="link" size="sm" className="p-0 me-2" title="แก้ไข"
                                          onClick={() => setLicModal({ ...l, file: null })}>
                                          <i className="ri-edit-line" />
                                        </Button>
                                        <Button variant="link" size="sm" className="p-0 text-danger" title="ลบ"
                                          onClick={() => confirmDelete('ลบใบอนุญาตนี้?', `/api/hr/me/licenses/${l.hl_id}`)}>
                                          <i className="ri-delete-bin-line" />
                                        </Button>
                                      </>
                                    ) : <span className="text-muted">งานบุคลากรบันทึก</span>}
                                  </td>
                                </tr>
                              )
                            })}
                          </tbody>
                        </Table>
                      </Tab.Pane>

                      <Tab.Pane eventKey="documents">
                        <Form onSubmit={handleUploadDocument} className="border rounded p-3 mb-3">
                          <Row className="g-2">
                            <Col md={3}>
                              <Form.Label className="fs-sm mb-1">ประเภทเอกสาร</Form.Label>
                              <Form.Control size="sm" placeholder="เช่น สำเนาวุฒิ, ใบอนุญาต" value={docForm.hd_type}
                                onChange={e => setDocForm(f => ({ ...f, hd_type: e.target.value }))} />
                            </Col>
                            <Col md={3}>
                              <Form.Label className="fs-sm mb-1">ชื่อเอกสาร</Form.Label>
                              <Form.Control size="sm" required value={docForm.hd_title}
                                onChange={e => setDocForm(f => ({ ...f, hd_title: e.target.value }))} />
                            </Col>
                            <Col md={3}>
                              <Form.Label className="fs-sm mb-1">วันออกเอกสาร</Form.Label>
                              <ThaiDateInput size="sm" value={docForm.hd_issue_date}
                                onChange={v => setDocForm(f => ({ ...f, hd_issue_date: v }))} />
                            </Col>
                            <Col md={3}>
                              <Form.Label className="fs-sm mb-1">วันหมดอายุ</Form.Label>
                              <ThaiDateInput size="sm" value={docForm.hd_expire_date}
                                onChange={v => setDocForm(f => ({ ...f, hd_expire_date: v }))} />
                            </Col>
                            <Col md={9}>
                              <Form.Label className="fs-sm mb-1">ไฟล์ (PDF/Word/รูปภาพ ≤ 10MB)</Form.Label>
                              <Form.Control size="sm" type="file" required accept=".pdf,.doc,.docx,image/*"
                                onChange={e => setDocForm(f => ({ ...f, file: e.target.files[0] || null }))} />
                            </Col>
                            <Col md={3} className="d-flex align-items-end">
                              <Button type="submit" size="sm" variant="primary" className="w-100" disabled={uploading}>
                                {uploading ? <Spinner size="sm" /> : <><i className="ri-upload-2-line me-1" />อัปโหลด</>}
                              </Button>
                            </Col>
                          </Row>
                          <div className="text-muted fs-sm mt-2">
                            วันออกเอกสารและวันหมดอายุไม่บังคับกรอก — กรอกเมื่อเป็นเอกสารที่มีอายุ เช่น ใบอนุญาตประกอบวิชาชีพ
                          </div>
                        </Form>
                        <Table responsive hover className="mb-0 fs-sm">
                          <thead className="table-light">
                            <tr>
                              <th>ประเภท</th><th>ชื่อเอกสาร</th><th>วันออก</th><th>วันหมดอายุ</th>
                              <th>อัปโหลดเมื่อ</th><th>โดย</th><th style={{ width: 70 }}></th>
                            </tr>
                          </thead>
                          <tbody>
                            {(data.documents || []).length === 0 ? (
                              <tr><td colSpan={7} className="text-center text-muted py-4">ยังไม่มีเอกสาร</td></tr>
                            ) : data.documents.map(d => {
                              const exp = docExpiryState(d.hd_expire_date)
                              return (
                                <tr key={d.hd_id}>
                                  <td>{d.hd_type || '-'}</td>
                                  <td>{d.hd_title}</td>
                                  <td>{d.hd_issue_date ? fmtDateTH(d.hd_issue_date) : '-'}</td>
                                  <td>
                                    {d.hd_expire_date ? fmtDateTH(d.hd_expire_date) : '-'}
                                    {exp && <Badge bg={exp.bg} className="ms-1">{exp.label}</Badge>}
                                  </td>
                                  <td>{fmtDateTH(d.created_at)}</td>
                                  <td>{d.uploaded_by_name || '-'}</td>
                                  <td className="text-end text-nowrap">
                                    <a className="me-2" href={`${UPLOAD_BASE}/uploads/hr_documents/${d.hd_filename}`}
                                      target="_blank" rel="noreferrer" title="เปิดไฟล์">
                                      <i className="ri-download-2-line" />
                                    </a>
                                    {/* ใบที่งานบุคลากรใส่เข้าแฟ้ม (สัญญาจ้าง ฯลฯ) เป็นเอกสารของฝ่าย ถอนออกเองไม่ได้ */}
                                    {d.uploaded_by === data.t_id && (
                                      <>
                                        <Button variant="link" size="sm" className="p-0 me-2" title="แก้ไข"
                                          onClick={() => setDocModal({ ...d, file: null })}>
                                          <i className="ri-edit-line" />
                                        </Button>
                                        <Button variant="link" size="sm" className="p-0 text-danger" title="ลบ"
                                          onClick={() => confirmDelete('ลบเอกสารนี้?', `/api/hr/me/documents/${d.hd_id}`)}>
                                          <i className="ri-delete-bin-line" />
                                        </Button>
                                      </>
                                    )}
                                  </td>
                                </tr>
                              )
                            })}
                          </tbody>
                        </Table>
                      </Tab.Pane>
                    </Tab.Content>
                  </Card.Body>
                </Card>
              </Tab.Container>
            </>
          )}
        </Container>
      </div>

      {/* ---- Education modal ---- */}
      <Modal show={!!eduModal} onHide={() => setEduModal(null)} centered>
        <Form onSubmit={handleSaveEducation}>
          <Modal.Header closeButton>
            <Modal.Title className="fs-6">{eduModal?.he_id ? 'แก้ไขวุฒิการศึกษา' : 'เพิ่มวุฒิการศึกษา'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Row className="g-2">
              <Col md={6}>
                <Form.Label className="fs-sm mb-1">ระดับ</Form.Label>
                <Form.Control size="sm" placeholder="เช่น ปริญญาตรี" value={eduModal?.he_level || ''}
                  onChange={e => setEduModal(m => ({ ...m, he_level: e.target.value }))} />
              </Col>
              <Col md={6}>
                <Form.Label className="fs-sm mb-1">วุฒิ/ปริญญา</Form.Label>
                <Form.Control size="sm" value={eduModal?.he_degree || ''}
                  onChange={e => setEduModal(m => ({ ...m, he_degree: e.target.value }))} />
              </Col>
              <Col md={6}>
                <Form.Label className="fs-sm mb-1">สาขา</Form.Label>
                <Form.Control size="sm" value={eduModal?.he_major || ''}
                  onChange={e => setEduModal(m => ({ ...m, he_major: e.target.value }))} />
              </Col>
              <Col md={6}>
                <Form.Label className="fs-sm mb-1">ปีที่จบ (พ.ศ.)</Form.Label>
                <Form.Control size="sm" maxLength={10} value={eduModal?.he_grad_year || ''}
                  onChange={e => setEduModal(m => ({ ...m, he_grad_year: e.target.value }))} />
              </Col>
              <Col md={12}>
                <Form.Label className="fs-sm mb-1">สถาบัน</Form.Label>
                <Form.Control size="sm" value={eduModal?.he_institution || ''}
                  onChange={e => setEduModal(m => ({ ...m, he_institution: e.target.value }))} />
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button size="sm" variant="secondary" onClick={() => setEduModal(null)}>ยกเลิก</Button>
            <Button size="sm" type="submit" variant="primary" disabled={savingItem}>
              {savingItem ? <Spinner size="sm" /> : 'บันทึก'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* ---- Work history modal ---- */}
      <Modal show={!!workModal} onHide={() => setWorkModal(null)} centered>
        <Form onSubmit={handleSaveWork}>
          <Modal.Header closeButton>
            <Modal.Title className="fs-6">{workModal?.hw_id ? 'แก้ไขประวัติการทำงาน' : 'เพิ่มประวัติการทำงาน'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Row className="g-2">
              <Col md={6}>
                <Form.Label className="fs-sm mb-1">ตำแหน่ง</Form.Label>
                <Form.Control size="sm" value={workModal?.hw_position || ''}
                  onChange={e => setWorkModal(m => ({ ...m, hw_position: e.target.value }))} />
              </Col>
              <Col md={6}>
                <Form.Label className="fs-sm mb-1">หน่วยงาน</Form.Label>
                <Form.Control size="sm" value={workModal?.hw_organization || ''}
                  onChange={e => setWorkModal(m => ({ ...m, hw_organization: e.target.value }))} />
              </Col>
              <Col md={6}>
                <Form.Label className="fs-sm mb-1">เริ่ม</Form.Label>
                <ThaiDateInput size="sm" value={workModal?.hw_start_date || ''}
                  onChange={v => setWorkModal(m => ({ ...m, hw_start_date: v }))} />
              </Col>
              <Col md={6}>
                <Form.Label className="fs-sm mb-1">สิ้นสุด (ว่างไว้ = ปัจจุบัน)</Form.Label>
                <ThaiDateInput size="sm" value={workModal?.hw_end_date || ''}
                  onChange={v => setWorkModal(m => ({ ...m, hw_end_date: v }))} />
              </Col>
              <Col md={12}>
                <Form.Label className="fs-sm mb-1">หมายเหตุ</Form.Label>
                <Form.Control size="sm" as="textarea" rows={2} value={workModal?.hw_note || ''}
                  onChange={e => setWorkModal(m => ({ ...m, hw_note: e.target.value }))} />
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button size="sm" variant="secondary" onClick={() => setWorkModal(null)}>ยกเลิก</Button>
            <Button size="sm" type="submit" variant="primary" disabled={savingItem}>
              {savingItem ? <Spinner size="sm" /> : 'บันทึก'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* ---- License modal ---- */}
      <Modal show={!!licModal} onHide={() => setLicModal(null)} centered>
        <Form onSubmit={handleSaveLicense}>
          <Modal.Header closeButton>
            <Modal.Title className="fs-6">
              {licModal?.hl_id ? 'แก้ไขใบอนุญาตประกอบวิชาชีพ' : 'เพิ่มใบอนุญาตประกอบวิชาชีพ'}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Row className="g-2">
              <Col md={6}>
                <Form.Label className="fs-sm mb-1">ประเภทใบอนุญาต</Form.Label>
                <Form.Select size="sm" value={licModal?.hl_type || 'teacher'}
                  onChange={e => setLicModal(m => ({ ...m, hl_type: e.target.value }))}>
                  {Object.entries(LICENSE_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </Form.Select>
              </Col>
              <Col md={6}>
                <Form.Label className="fs-sm mb-1">เลขที่ใบอนุญาต</Form.Label>
                <Form.Control size="sm" required maxLength={50} value={licModal?.hl_number || ''}
                  onChange={e => setLicModal(m => ({ ...m, hl_number: e.target.value }))} />
              </Col>
              <Col md={6}>
                <Form.Label className="fs-sm mb-1">วันออกใบอนุญาต</Form.Label>
                <ThaiDateInput size="sm" value={licModal?.hl_issue_date || ''}
                  onChange={v => setLicModal(m => ({ ...m, hl_issue_date: v }))} />
              </Col>
              <Col md={6}>
                <Form.Label className="fs-sm mb-1">วันหมดอายุ</Form.Label>
                <ThaiDateInput size="sm" value={licModal?.hl_expire_date || ''}
                  onChange={v => setLicModal(m => ({ ...m, hl_expire_date: v }))} />
              </Col>
              <Col md={12}>
                <Form.Label className="fs-sm mb-1">ไฟล์สำเนาใบอนุญาต (PDF/รูปภาพ ≤ 10MB)</Form.Label>
                <Form.Control size="sm" type="file" accept=".pdf,.doc,.docx,image/*"
                  onChange={e => setLicModal(m => ({ ...m, file: e.target.files[0] || null }))} />
                <div className="text-muted fs-sm mt-1">
                  {licModal?.hl_filename
                    ? `ไฟล์ปัจจุบัน: ${licModal.hl_original_name || licModal.hl_filename} — เลือกไฟล์ใหม่เพื่อแทนที่`
                    : 'ไม่บังคับแนบ — บันทึกเลขที่กับวันหมดอายุไว้ก่อน แล้วค่อยแนบสแกนทีหลังได้'}
                </div>
              </Col>
              <Col md={12}>
                <Form.Label className="fs-sm mb-1">หมายเหตุ</Form.Label>
                <Form.Control size="sm" as="textarea" rows={2} value={licModal?.hl_note || ''}
                  onChange={e => setLicModal(m => ({ ...m, hl_note: e.target.value }))} />
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button size="sm" variant="secondary" onClick={() => setLicModal(null)}>ยกเลิก</Button>
            <Button size="sm" type="submit" variant="primary" disabled={savingItem}>
              {savingItem ? <Spinner size="sm" /> : 'บันทึก'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* ---- Document edit modal ---- */}
      <Modal show={!!docModal} onHide={() => setDocModal(null)} centered>
        <Form onSubmit={handleSaveDocument}>
          <Modal.Header closeButton>
            <Modal.Title className="fs-6">แก้ไขเอกสาร</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Row className="g-2">
              <Col md={6}>
                <Form.Label className="fs-sm mb-1">ประเภทเอกสาร</Form.Label>
                <Form.Control size="sm" value={docModal?.hd_type || ''}
                  onChange={e => setDocModal(m => ({ ...m, hd_type: e.target.value }))} />
              </Col>
              <Col md={6}>
                <Form.Label className="fs-sm mb-1">ชื่อเอกสาร</Form.Label>
                <Form.Control size="sm" required value={docModal?.hd_title || ''}
                  onChange={e => setDocModal(m => ({ ...m, hd_title: e.target.value }))} />
              </Col>
              <Col md={6}>
                <Form.Label className="fs-sm mb-1">วันออกเอกสาร</Form.Label>
                <ThaiDateInput size="sm" value={docModal?.hd_issue_date || ''}
                  onChange={v => setDocModal(m => ({ ...m, hd_issue_date: v }))} />
              </Col>
              <Col md={6}>
                <Form.Label className="fs-sm mb-1">วันหมดอายุ</Form.Label>
                <ThaiDateInput size="sm" value={docModal?.hd_expire_date || ''}
                  onChange={v => setDocModal(m => ({ ...m, hd_expire_date: v }))} />
              </Col>
              <Col md={12}>
                <Form.Label className="fs-sm mb-1">เปลี่ยนไฟล์ (ไม่เลือก = ใช้ไฟล์เดิม)</Form.Label>
                <Form.Control size="sm" type="file" accept=".pdf,.doc,.docx,image/*"
                  onChange={e => setDocModal(m => ({ ...m, file: e.target.files[0] || null }))} />
                <div className="text-muted fs-sm mt-1">
                  ไฟล์ปัจจุบัน: {docModal?.hd_original_name || docModal?.hd_filename}
                </div>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button size="sm" variant="secondary" onClick={() => setDocModal(null)}>ยกเลิก</Button>
            <Button size="sm" type="submit" variant="primary" disabled={savingItem}>
              {savingItem ? <Spinner size="sm" /> : 'บันทึก'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Footer />
    </>
  )
}
